import { observer } from "mobx-react-lite";
import React from "react";
import { useApp } from "../../../context";
import { useNavigate } from "react-router";
import WaveSurfer from "wavesurfer.js";
import { animated, useSprings } from "react-spring";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { Icon } from "@opendash/icons";
import Steps from "rc-steps";

const BASE_SCORE = 100;
const MAX_MULTIPLIER = 3;
const DECAY_SECONDS = 45;
const MAX_REPLAYS = 3;
const STREAK_BONUS_THRESHOLD = 3;
const HISTORY_KEY = "antiscam_game_history";

function calcMultiplier(elapsed: number) {
  return Math.max(1, MAX_MULTIPLIER - (elapsed / DECAY_SECONDS) * (MAX_MULTIPLIER - 1));
}

function calcScore(elapsed: number, streak: number) {
  const base = Math.round(BASE_SCORE * calcMultiplier(elapsed));
  const streakBonus = streak >= 5 ? 1.5 : streak >= STREAK_BONUS_THRESHOLD ? 1.25 : 1;
  return Math.round(base * streakBonus);
}

interface HistoryEntry {
  date: string;
  score: number;
  correct: number;
  total: number;
}

function loadHistory(): HistoryEntry[] {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveHistory(entry: HistoryEntry) {
  const history = loadHistory();
  history.unshift(entry);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, 10)));
}

const useStyles = makeStyles((theme) => ({
  questionContainer: {
    minHeight: "50vh",
    position: "relative",
    overflow: "hidden",
  },
  question: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "auto",
    minHeight: "100%",
  },
  questionInner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "auto",
    minHeight: "100%",
  },
  questionContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    padding: theme.spacing(2, 0),
  },
  questionAudio: {
    width: "100%",
    marginBottom: 12,
    border: "1px solid #ccc",
    borderRadius: "4px",
  },
  audioControls: {
    display: "flex",
    gap: theme.spacing(1),
    margin: "8px auto 10px",
    width: "fit-content",
  },
  choiceButtons: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  choiceButton: {
    minWidth: "150px",
    padding: theme.spacing(1, 3),
  },
}));

export const GameRoute = observer(function GameRoute() {
  const app = useApp();
  const navigate = useNavigate();
  const classes = useStyles();

  const questions = app.benchmarks.questions;

  const [index, setIndex] = React.useState(0);
  const [answers, setAnswers] = React.useState<Record<string, 1 | 2>>({});
  const [scores, setScores] = React.useState<Record<string, number>>({});
  const [feedback, setFeedback] = React.useState<Record<string, "correct" | "wrong">>({});
  const [elapsed, setElapsed] = React.useState(0);
  const [streak, setStreak] = React.useState(0);
  const [replayCounts, setReplayCounts] = React.useState<Record<string, number>>({});
  const [copied, setCopied] = React.useState(false);
  const [historySaved, setHistorySaved] = React.useState(false);

  const [loadedCount, setLoadedCount] = React.useState(0);
  const [audioPlayback, setAudioPlayback] = React.useState<
    Record<string, { isPlaying: boolean; currentTime: number; duration: number }>
  >({});
  const [containerHeight, setContainerHeight] = React.useState(0);

  const wavesurferRefs = React.useRef<Record<string, WaveSurfer>>({});
  const containerRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const slideRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedRef = React.useRef(0);
  const streakRef = React.useRef(0);

  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      Object.values(wavesurferRefs.current).forEach((ws) => ws.destroy());
    };
  }, []);

  React.useEffect(() => {
    questions.forEach((question) => {
      if (!question?.audio) return;
      if (wavesurferRefs.current[question.objectId]) return;

      const container = containerRefs.current[question.objectId];
      if (!container) return;

      const wavesurfer = WaveSurfer.create({
        container,
        waveColor: "#4fc3f7",
        progressColor: "#29b6f6",
        cursorColor: "#1976d2",
        barWidth: 2,
        height: 120,
      });

      wavesurfer.load((question.audio as any).url);
      wavesurferRefs.current[question.objectId] = wavesurfer;

      wavesurfer.on("ready", () => {
        setLoadedCount((prev) => prev + 1);
        setAudioPlayback((prev) => ({
          ...prev,
          [question.objectId]: {
            duration: wavesurfer.getDuration(),
            currentTime: 0,
            isPlaying: false,
          },
        }));
      });

      wavesurfer.on("play", () => {
        setAudioPlayback((prev) => ({
          ...prev,
          [question.objectId]: { ...prev[question.objectId], isPlaying: true },
        }));
      });

      wavesurfer.on("pause", () => {
        setAudioPlayback((prev) => ({
          ...prev,
          [question.objectId]: { ...prev[question.objectId], isPlaying: false },
        }));
      });

      wavesurfer.on("audioprocess", () => {
        setAudioPlayback((prev) => ({
          ...prev,
          [question.objectId]: {
            ...prev[question.objectId],
            currentTime: wavesurfer.getCurrentTime(),
          },
        }));
      });
    });
  }, []);

  React.useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    elapsedRef.current = 0;
    setElapsed(0);

    if (index < questions.length) {
      timerRef.current = setInterval(() => {
        elapsedRef.current += 1;
        setElapsed(elapsedRef.current);
      }, 1000);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [index]);

  React.useEffect(() => {
    const update = () => {
      let max = 0;
      slideRefs.current.forEach((ref) => {
        if (ref) max = Math.max(max, ref.scrollHeight);
      });
      if (max > 0) setContainerHeight(max);
    };
    const t = setTimeout(update, 100);
    window.addEventListener("resize", update);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", update);
    };
  }, [index, audioPlayback, feedback, historySaved]);

  const questionsWithAudio = questions.filter((q) => q.audio).length;
  const allLoaded = questionsWithAudio === 0 || loadedCount >= questionsWithAudio;

  const totalScore = Object.values(scores).reduce((a, b) => a + b, 0);
  const correctCount = questions.filter((q) => {
    const a = answers[q.objectId];
    return a !== undefined && a === (q.synthetic ? 2 : 1);
  }).length;

  const changeQuestion = (questionIndex: number) => {
    wavesurferRefs.current[questions[index]?.objectId]?.pause();
    setIndex(questionIndex);
    // @ts-ignore
    set((i: number) => ({ x: (i - questionIndex) * window.innerWidth }));
  };

  const handleAnswer = (questionIndex: number, answer: 1 | 2) => {
    const question = questions[questionIndex];
    if (!question || answers[question.objectId] !== undefined) return;

    if (timerRef.current) clearInterval(timerRef.current);

    const isCorrect = answer === (question.synthetic ? 2 : 1);

    const newStreak = isCorrect ? streakRef.current + 1 : 0;
    streakRef.current = newStreak;
    setStreak(newStreak);

    const score = isCorrect ? calcScore(elapsedRef.current, newStreak) : 0;

    wavesurferRefs.current[question.objectId]?.pause();
    setAnswers((prev) => ({ ...prev, [question.objectId]: answer }));
    setScores((prev) => ({ ...prev, [question.objectId]: score }));
    setFeedback((prev) => ({
      ...prev,
      [question.objectId]: isCorrect ? "correct" : "wrong",
    }));

    // Save history when game finishes
    if (questionIndex === questions.length - 1) {
      const finalScore = Object.values(scores).reduce((a, b) => a + b, 0) + score;
      const finalCorrect = questions.filter((q, qi) => {
        if (qi === questionIndex) return isCorrect;
        const a = answers[q.objectId];
        return a !== undefined && a === (q.synthetic ? 2 : 1);
      }).length;
      saveHistory({
        date: new Date().toLocaleDateString("de-DE"),
        score: finalScore,
        correct: finalCorrect,
        total: questions.length,
      });
      setHistorySaved(true);
    }

    setTimeout(() => changeQuestion(questionIndex + 1), 800);
  };

  const handleShare = () => {
    const emoji = totalScore >= 800 ? "🏆" : totalScore >= 500 ? "🥇" : totalScore >= 300 ? "👍" : "📚";
    const text = `${emoji} Ich habe ${totalScore} Punkte beim KI-Stimmen-Erkennungsspiel erreicht! (${correctCount}/${questions.length} richtig)\nKannst du das toppen? → ${window.location.origin}/benchmark`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const history = loadHistory().slice(1); // skip current game (just saved)

  const slides = [
    ...questions.map((question, questionIndex) => {
      const isAnswered = answers[question.objectId] !== undefined;
      const fb = feedback[question.objectId];
      const playback = audioPlayback[question.objectId];
      const replays = replayCounts[question.objectId] || 0;
      const replayExhausted = isAnswered ? false : replays >= MAX_REPLAYS;

      return (
        <div className={classes.questionInner}>
          <div className={classes.questionContent}>
            {/* Timer + multiplier + streak */}
            <Box display="flex" justifyContent="space-between" width="100%" mb={1}>
              <Typography variant="h6" style={{ fontFamily: "monospace" }}>
                ⏱{" "}
                {String(Math.floor(elapsed / 60)).padStart(2, "0")}:
                {String(elapsed % 60).padStart(2, "0")}
              </Typography>
              <Box display="flex" style={{ gap: 12, alignItems: "center" }}>
                {streak >= STREAK_BONUS_THRESHOLD && !isAnswered && (
                  <Typography variant="body2" style={{ color: "#ff9800", fontWeight: 700 }}>
                    🔥 {streak}× Streak
                  </Typography>
                )}
                {!isAnswered && (
                  <Typography variant="body2" color="textSecondary" style={{ alignSelf: "center" }}>
                    {calcMultiplier(elapsed).toFixed(1)}× Mult.
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Feedback badge */}
            {fb && (
              <Box
                mb={2}
                style={{
                  padding: "8px 24px",
                  borderRadius: 8,
                  background: fb === "correct" ? "#4caf50" : "#f44336",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 18,
                }}
              >
                {fb === "correct"
                  ? `✓ +${scores[question.objectId]} Punkte`
                  : "✗ Falsch"}
              </Box>
            )}

            {/* Waveform */}
            {question.audio && (
              <div className={classes.questionAudio}>
                <div
                  ref={(el) => {
                    containerRefs.current[question.objectId] = el;
                  }}
                  style={{ width: "100%", height: "120px" }}
                />
                <div className={classes.audioControls}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const ws = wavesurferRefs.current[question.objectId];
                      if (ws) playback?.isPlaying ? ws.pause() : ws.play();
                    }}
                  >
                    {playback?.isPlaying ? "Pause" : "Play"}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={replayExhausted}
                    title={replayExhausted ? "Keine Wiederholungen mehr" : `Noch ${MAX_REPLAYS - replays} Wiederholungen`}
                    onClick={() => {
                      const ws = wavesurferRefs.current[question.objectId];
                      if (ws) {
                        ws.seekTo(0);
                        setAudioPlayback((prev) => ({
                          ...prev,
                          [question.objectId]: { ...prev[question.objectId], currentTime: 0 },
                        }));
                        if (!isAnswered) {
                          setReplayCounts((prev) => ({
                            ...prev,
                            [question.objectId]: (prev[question.objectId] || 0) + 1,
                          }));
                        }
                      }
                    }}
                  >
                    Restart {!isAnswered && `(${MAX_REPLAYS - replays})`}
                  </Button>
                </div>
              </div>
            )}

            {/* Answer buttons */}
            <Typography variant="subtitle2" align="center" gutterBottom>
              Ist diese Stimme echt oder KI-generiert?
            </Typography>
            <div className={classes.choiceButtons}>
              <Button
                className={classes.choiceButton}
                variant={answers[question.objectId] === 1 ? "contained" : "outlined"}
                color={answers[question.objectId] === 1 ? "primary" : "default"}
                disabled={isAnswered}
                onClick={() => handleAnswer(questionIndex, 1)}
              >
                Echt
              </Button>
              <Button
                className={classes.choiceButton}
                variant={answers[question.objectId] === 2 ? "contained" : "outlined"}
                color={answers[question.objectId] === 2 ? "primary" : "default"}
                disabled={isAnswered}
                onClick={() => handleAnswer(questionIndex, 2)}
              >
                KI-generiert
              </Button>
            </div>
          </div>
        </div>
      );
    }),

    // Results slide
    <div className={classes.questionInner}>
      <div className={classes.questionContent}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          style={{ gap: 24, padding: "32px 16px", maxWidth: 480, margin: "0 auto" }}
        >
          <Typography variant="h2" style={{ fontSize: 64, lineHeight: 1 }}>
            {totalScore >= 800 ? "🏆" : totalScore >= 500 ? "🥇" : totalScore >= 300 ? "👍" : "📚"}
          </Typography>
          <Typography variant="h5" align="center" style={{ fontWeight: 700 }}>
            Spiel beendet!
          </Typography>
          <Box
            style={{
              background: "rgba(0,0,0,0.06)",
              borderRadius: 16,
              padding: "24px 40px",
              textAlign: "center",
              width: "100%",
            }}
          >
            <Typography variant="h3" style={{ fontWeight: 800 }}>
              {totalScore}
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              Punkte
            </Typography>
            <Divider style={{ margin: "16px 0" }} />
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              {correctCount} / {questions.length}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              richtig erkannt
            </Typography>
          </Box>

          {/* Per-question recap dots */}
          <Box display="flex" style={{ gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
            {questions.map((q, qi) => {
              const isCorrect = answers[q.objectId] === (q.synthetic ? 2 : 1);
              const pts = scores[q.objectId] ?? 0;
              return (
                <Box
                  key={q.objectId}
                  onClick={() => changeQuestion(qi)}
                  title={`Frage ${qi + 1}: ${pts} Punkte`}
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    background: isCorrect ? "#4caf50" : "#f44336",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 11,
                    cursor: "pointer",
                    lineHeight: 1.2,
                  }}
                >
                  <span>{qi + 1}</span>
                  <span>{pts}</span>
                </Box>
              );
            })}
          </Box>

          {/* Share button */}
          <Button variant="outlined" onClick={handleShare} style={{ minWidth: 180 }}>
            {copied ? "✓ Kopiert!" : "Ergebnis teilen"}
          </Button>

          <Box display="flex" style={{ gap: 12 }}>
            <Button variant="contained" color="primary" onClick={() => navigate("/benchmark")}>
              Nochmal spielen
            </Button>
          </Box>

          {/* Personal history */}
          {history.length > 0 && (
            <Box style={{ width: "100%" }}>
              <Divider style={{ margin: "8px 0 16px" }} />
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Deine letzten Spiele
              </Typography>
              <Box style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {history.slice(0, 5).map((h, i) => (
                  <Box
                    key={i}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    style={{
                      padding: "6px 10px",
                      borderRadius: 8,
                      background: "rgba(0,0,0,0.04)",
                      fontSize: 13,
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">{h.date}</Typography>
                    <Typography variant="body2">{h.correct}/{h.total} richtig</Typography>
                    <Typography variant="body2" style={{ fontWeight: 700 }}>{h.score} Pts</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </div>
    </div>,
  ];

  const [props, set] = useSprings(slides.length, (i) => ({
    x: i * window.innerWidth,
  }));

  return (
    <Card style={{ padding: 20 }}>
      {!allLoaded && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          style={{ padding: "40px 16px", gap: 16 }}
        >
          <CircularProgress
            variant="determinate"
            value={Math.round((loadedCount / questionsWithAudio) * 100)}
          />
          <Typography variant="body2" color="textSecondary">
            Audio wird geladen... {loadedCount} / {questionsWithAudio}
          </Typography>
        </Box>
      )}

      {allLoaded && (
        <Box display="flex" alignItems="center" mb={1} style={{ gap: 16 }}>
          <nav style={{ flex: 1, minWidth: 0 }}>
            <Steps current={index}>
              {questions.map((question, qi) => {
                const a = answers[question.objectId];
                const isCorrect = a !== undefined && a === (question.synthetic ? 2 : 1);
                return (
                  <Steps.Step
                    key={qi}
                    title=""
                    icon={
                      a !== undefined ? (
                        <Icon icon={isCorrect ? "fa:check" : "fa:times"} />
                      ) : (
                        <Icon icon="fa:question-circle" />
                      )
                    }
                    onClick={() => changeQuestion(qi)}
                    style={{ cursor: "pointer" }}
                  />
                );
              })}
            </Steps>
          </nav>
          <Box display="flex" style={{ gap: 8, alignItems: "center" }}>
            {streak >= STREAK_BONUS_THRESHOLD && index < questions.length && (
              <Typography variant="body2" style={{ color: "#ff9800", fontWeight: 700, whiteSpace: "nowrap" }}>
                🔥{streak}
              </Typography>
            )}
            <Typography variant="h6" style={{ fontWeight: 700, whiteSpace: "nowrap" }}>
              {totalScore} Pts
            </Typography>
          </Box>
        </Box>
      )}

      <div
        className={classes.questionContainer}
        style={{
          height: allLoaded ? containerHeight || "auto" : 1,
          visibility: allLoaded ? "visible" : "hidden",
        }}
      >
        {slides.map((slide, slideIndex) => {
          const { x } = props[slideIndex];
          return (
            <animated.div
              key={slideIndex}
              ref={(el) => {
                slideRefs.current[slideIndex] = el;
              }}
              className={classes.question}
              style={{
                transform: x.interpolate((x) => `translate3d(${x}px,0,0)`),
              }}
              children={slide}
            />
          );
        })}
      </div>
    </Card>
  );
});
